// ── PRODUCTION PER UNIT — what a production report can actually be filled with ─
//
// Kevin, 2026-09-30: *"Maybe display all of the data we have for production and if we agree
// with it and know it's all correct, we can hit a button to fill production report."*
// Kevin, 2026-10-02, choosing how a multi-unit farm should behave: *split per unit from
// machine data where it exists.*
//
// ⚠ THE FORM IS FILED PER UNIT AND THE EVIDENCE TOTALS PER COUNTY. That mismatch is the
// whole reason this module exists, and it was found by reading a real form rather than by
// reasoning about one. The NAU "Actual Production History (APH) Database" pre-prints, on its
// "Production Reporting Unit Summary" page, one block per unit:
//
//     WHEAT 0001-0007-000
//     051 NON IRR/W 1.0000 240.52          <- county, practice/type, share, ACRES
//     BU
//     RP
//     , , .                                <- the three blanks: yield/acre, production, record type
//     Location: Fsn/Tract: 7648 SECTION 7
//
// So the three things a fill must state are PER UNIT: Yield Per Acre, Total Production, and
// the RMA Production Record Type. `productionEvidence.js` deliberately totals per COUNTY and
// resolves nothing; a county total covering two units is not a unit figure, and splitting it
// pro-rata on acres would put a modelled number on a federal form that sets the grower's APH
// for ten years. This module states a unit only where the EVIDENCE is per unit.
//
// ⚠ AND THE MEASUREMENT SAYS THAT IS A MINORITY TODAY — reported rather than smoothed over.
// Measured 2026-10-02 over the 14 CY2026 wheat farms with nothing else blocking a report:
//
//     every unit >=90% machine-covered   4 farms  (Atwater 98%, Bridgewater 96%,
//                                                  Meisenzahl 96%, Lawnel 93%)
//     partial                            1 farm   (Hart Family: 8 of 10 units covered)
//     no machine wheat yield at all      9 farms  (incl. Verratti and Ray Sick, the
//                                                  multi-unit farms this was meant to help)
//
// All four fully-covered farms are SINGLE-unit, where the farm total already is the unit
// total — so for them the machine is a cross-check, not a split. Hart is the farm this
// genuinely unlocks. Verratti and Ray Sick stay refused, and saying so is the point.
// ── WHERE THIS SITS, BECAUSE FOUR MODULES NOW TOUCH ONE FORM ─────────────────
// A rule defined twice is a rule that drifts, and this corner already has three owners. Each
// answers a different question and none of them answered THIS one:
//
//   productionEvidence.js   what every channel says, per COUNTY. Decides nothing.
//   productionReportLine.js WHICH basis you would file — DOCUMENTED (sold + stored + fed) or
//                           MONITOR (the machine alone). They measure the same harvest twice,
//                           so they are alternatives, never addends.
//   productionAllocation.js how to split ONE commingled total across units — RMA CIH ¶1715's
//                           four methods, refused until the grower has been asked.
//   productionUnits.js      (here) production stated PER UNIT where the evidence is already
//                           per unit, so there is nothing to allocate.
//
// ⚠ THE RELATIONSHIP TO ALLOCATION IS EXCLUSIVE, NOT ADDITIVE. If the machine states each
// unit, the production is not commingled and ¶1715 does not apply — the handbook's methods
// exist for a total that cannot be attributed. Running both would split a figure that was
// never pooled.
//
// ⚠ AND THIS IS THE **MONITOR** BASIS, WHICH IS NOT THE CLAIM OF RECORD. `productionReportLine`
// defaults to DOCUMENTED for a reason RMA's own vocabulary carries: a settlement record is
// type A, a machine estimate is type D. A per-unit monitor figure is the only per-unit
// evidence this book holds, which is why Kevin chose it (2026-10-02) — but it is labelled with
// that module's own `BASIS.monitor`, not presented as the record. A caller that has documented
// records for the farm is choosing between two measurements of one harvest, and that choice
// belongs to `productionReportLine`, not here.
"use strict";

const PR = require("./productionRecord.js");
const { BASIS } = require("./productionReportLine.js");
const { harvestFormOf } = require("./harvestForm.js");

const n = (v) => {
  if (v == null || v === "") return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};
const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
// ⚠ CLU ids arrive in BOTH cases in this book — `policy_fields` is 59% UPPER while
// `field_geometries` is all lower — so every comparison folds first. A naive compare reads
// one CLU as two, the join finds nothing, and the unit reads as having no machine data at
// all, which is the refusal this module hands back. Silent, and in the costly direction.
const cluKey = (v) => String(v == null ? "" : v).trim().toUpperCase();
// Integer hundredths, the same basis `productionEvidence` and `countyProduction` sum on, so
// a figure a human copies onto a form does not drift between the screen and the stamp.
const cents = (v) => Math.round((n(v) || 0) * 100);
const undo = (c) => Math.round(c) / 100;

// The unit as the form writes it: unit number, sub unit. `policy_fields` carries the two
// apart; the form prints them joined. Padded because the file stores "1" and the form prints
// "0001", and a key built from the raw value matches nothing.
const pad4 = (v) => String(v == null ? "" : v).trim().padStart(4, "0");
const unitKeyOf = (unitNumber, subUnit) => `${pad4(unitNumber)}-${pad4(subUnit)}`;
// ⚠ A UNIT NUMBER IS ONLY UNIQUE INSIDE ONE POLICY. Kevin, 2026-10-31, approving the fix: the
// same unit number on two policies of one farmer was merged into one unit — 40 such pairs in
// the CY2026 book — so one unit's acres and machine yield were reported on the other's form.
// Grouping is by (policy, unit, sub unit); `unitKey` stays the form's own "0001-0001" so the
// form pairing reads it, and `policyId` rides beside it. A row with no policy id (an older
// caller) groups as it always did.
const groupKeyOf = (policyId, unitNumber, subUnit) =>
  `${policyId == null ? "" : String(policyId)}|${unitKeyOf(unitNumber, subUnit)}`;

// ⚠ THE CROP MATCHES EXACTLY. It was a substring test, and "CORN SILAGE" contains "CORN", so a
// corn grain unit summed the chopper's silage tonnage as bushels. Every label in the book
// (jd_field_ftf, policy_fields, crop_coverage_units) is the crop name itself, measured
// 2026-10-31, so an exact compare loses nothing.
const sameCrop = (a, CROP) => up(a) === CROP;
// Corn type 026 / GSS is silage (claimCrops.js isSilageType; that module is ESM). Silage has
// its own APH database in tons, so its acres are not corn grain acres — 73 CY2026 corn sub
// units carry both, and counting the silage acres put them in the grain yield's denominator.
const isSilageType = (t) => ["026", "GSS", "SILAGE", "SILAGE-026"].indexOf(up(t)) >= 0;

// ── WHICH UNIT ROWS STATE A UNIT'S ACREAGE, AND WHICH RESTATE IT ─────────────
// ⚠ AN SCO/ECO ROW RESTATES THE SAME PHYSICAL ACRES, so summing `crop_coverage_units.acres`
// over a unit runs 2-3x high. This is not a hypothetical: the first cut of this module summed
// them and read BRIDGEWATER's single wheat unit at 1,471.24 acres against a real 735.62
// (base + one overlay) and MEISENZAHL at 554.34 against 184.78 (base + SCO + ECO). Machine
// coverage then read 48% and 32% instead of 96%, which is the direction that refuses a farm
// whose evidence is complete. It was caught only because the row prints the carrier figure
// and our own field sum SIDE BY SIDE rather than trusting either.
//
// ⚠ AND `isBasePlan` DOES NOT APPLY HERE. That rule tests numeric plan codes ("01","02",…)
// on `crop_coverage.mpci_plan_code`; `crop_coverage_units.plan` holds NAMES. Measured over
// the whole table 2026-10-02 — 6,597 rows, every distinct value listed:
//
//     RP 3699 · APH 545 · YP 298          <- state the unit's own acres
//     ECO-RP 1306 · SCO-RP 677 · ECO-YP 70 · SCO-YP 2   <- restate them
//
// ⚠ AND ON A SHELL UNIT THE OVERLAY IS NOT EVEN A RESTATEMENT. Of 1,279 units carrying both,
// 1,235 have the overlay matching the base acreage exactly and 44 do NOT — and those 44 are
// the `…-0000` shells this book already documents: one CORN 0003-0000 reads base 59.3 against
// SCO/ECO 3,536.9, the whole policy's acreage sitting on a unit that rates nothing. Taking a
// max, or an average, or the first row would land on that figure.
//
// Resolution is by MEMBERSHIP, never by similarity — the rule `basisMarketAliases` and
// `STAGE_BY_FOLDER` both follow. A plan name in NEITHER set refuses its unit and is reported,
// so a product RMA introduces next year surfaces as a question rather than being absorbed as
// whichever default happened to be safer.
const BASE_UNIT_PLANS = Object.freeze(["RP", "APH", "YP"]);
const OVERLAY_UNIT_PLANS = Object.freeze(["SCO-RP", "ECO-RP", "SCO-YP", "ECO-YP"]);
const unitPlanClass = (plan) => {
  const p = up(plan);
  if (BASE_UNIT_PLANS.indexOf(p) >= 0) return "base";
  if (OVERLAY_UNIT_PLANS.indexOf(p) >= 0) return "overlay";
  return "unknown";
};

// ⚠ HOW MUCH OF A UNIT THE MACHINE MUST COVER BEFORE ITS TOTAL IS THE UNIT'S TOTAL.
// There is no threshold here, and that is deliberate. A combine never covers 100% of insured
// acres — headlands, overlaps and unmapped passes put the live figures at 80-98% — so any cut
// would be a number nobody sourced, standing between a grower and a ten-year APH. What the
// rule does instead is REPORT coverage per unit and let the agent read it before stamping,
// which is the same shape `api/_acreage.js` already uses: `action=preview` returns the review
// and stamps nothing. A caller that wants to fail closed passes `minCoverage` explicitly.
// ⚠ THE PRODUCTION FILL NOW DOES (2026-10-18): Kevin set 90% of insured acres, and
// `api/_production-fill.js` passes it as MIN_MACHINE_COVERAGE. The yield per acre below divides
// by INSURED acres, so a partly-mapped unit with no floor understated the APH it would set.
//
// What IS refused without argument is a unit the machine never touched: see `NO_COVERAGE`.
const NO_COVERAGE = 0;
// The same 2% `productionEvidence` already allows when it compares machine acres against
// insured acres, reused rather than re-chosen so the two surfaces cannot disagree about when
// a farm has run more ground than it insures.
const OVER_COVER_TOL = 1.02;

/**
 * buildProductionUnits({ crop, cropYear, policyFields, jdFtf, coverageUnits, minCoverage })
 *
 *   policyFields   policy_fields rows — clu_id, crop, crop_year, unit_number, sub_unit, acres
 *   jdFtf          jd_field_ftf rows, metric='yield' — clu_id, crop, operation_date, acres, mean_value
 *   coverageUnits  crop_coverage_units rows — unit_number, sub_unit, acres, county, is_prevent_plant
 *   minCoverage    optional 0-1. Omitted, nothing is refused for being thin — it is REPORTED.
 *
 * Returns one row per insured unit, each saying what it can be filled with and why not when
 * it cannot. It picks nothing and averages nothing.
 */
function buildProductionUnits({
  crop,
  cropYear,
  policyFields = [],
  jdFtf = [],
  coverageUnits = [],
  minCoverage = null,
} = {}) {
  const CROP = up(crop);
  const CY = Number(cropYear);
  const floor = n(minCoverage);

  // ── the insured units, and the CLUs inside each ────────────────────────────
  // ⚠ SCOPED (crop, crop year), NEVER THE FARM ALONE. A CLU insured to corn is not insured
  // ground for a wheat production report, and a first measurement scoped only by farmer found
  // essentially no off-book acres and was wrong — the same trap `productionEvidence` records
  // for Gary Swede, where the excess was his own ground insured to another crop.
  const byUnit = new Map();
  let fieldsNoUnit = 0, silageFieldsSkipped = 0;
  for (const p of policyFields) {
    if (Number(p.crop_year) !== CY) continue;
    if (!sameCrop(p.crop, CROP)) continue;
    if (CROP === "CORN" && isSilageType(p.type_code)) { silageFieldsSkipped++; continue; }
    const clu = cluKey(p.clu_id);
    if (!clu) continue;
    // ⚠ A FIELD WITH NO UNIT CANNOT BE PLACED, AND IT IS COUNTED RATHER THAN DROPPED.
    // `policy_fields.unit_number` is stamped by both syncs and was first-wins-wrong until
    // `pickFieldUnit` landed; a row that still carries none is ground we hold acreage for and
    // cannot attribute, which is a different fact from a unit with no machine data.
    if (!p.unit_number) { fieldsNoUnit++; continue; }
    const key = groupKeyOf(p.policy_id, p.unit_number, p.sub_unit);
    const e = byUnit.get(key) || {
      groupKey: key, unitKey: unitKeyOf(p.unit_number, p.sub_unit),
      policyId: p.policy_id == null ? null : p.policy_id,
      unitNumber: pad4(p.unit_number), subUnit: pad4(p.sub_unit),
      county: up(p.county) || null, insuredAcresFields: 0, clus: new Set(),
    };
    e.insuredAcresFields += n(p.acres) || 0;
    e.clus.add(clu);
    if (!e.county) e.county = up(p.county) || null;
    byUnit.set(key, e);
  }

  // ── what the carrier says each unit is, which is what the FORM pre-prints ──
  // ⚠ THE DENOMINATOR IS THE CARRIER'S ACREAGE, NOT THE SUM OF OUR FIELD ROWS. The form
  // prints its own acres per unit (Kennedy 0001-0007-000 reads 240.52) and the yield per acre
  // it wants is production over THAT figure. Dividing by our own field sum would print a
  // yield the form's own arithmetic contradicts, on the page an adjuster reads.
  const unitAcres = new Map();
  const ppAcresByUnit = new Map();       // groupKey -> prevented acres on the unit
  const unknownPlanUnits = new Map();     // groupKey -> the plan name we could not classify
  for (const u of coverageUnits) {
    if (Number(u.crop_year) !== CY && u.crop_year != null) continue;
    if (!sameCrop(u.crop, CROP)) continue;
    if (!u.unit_number) continue;
    if (CROP === "CORN" && isSilageType(u.type_code)) continue;
    const key = groupKeyOf(u.policy_id, u.unit_number, u.sub_unit);
    // ⚠ PREVENTED ACRES REPORT NO PRODUCTION. It is not zero production on planted ground —
    // nothing was planted — and a 0 there is a total loss on acres that never grew a crop.
    // ⚠ BUT ONLY THOSE ACRES. Kevin, 2026-10-31, approving the fix: Rain & Hail states a
    // unit's prevented acres as their own row beside the planted one, and taking any PP row as
    // the whole sub unit refused the planted acres too (121 CY2026 sub units carry both). The
    // PP row is set aside — it never enters the acreage below — and the unit is prevent plant
    // only when no planted acres are left on it.
    if (u.is_prevent_plant) { ppAcresByUnit.set(key, (ppAcresByUnit.get(key) || 0) + (n(u.acres) || 0)); continue; }
    const cls = unitPlanClass(u.plan);
    // An overlay restates acres this unit already states — see the note above. Skipped
    // silently because it is not a finding; it is the normal shape of every policy.
    if (cls === "overlay") continue;
    if (cls === "unknown") { unknownPlanUnits.set(key, up(u.plan) || "(blank)"); continue; }
    const e = unitAcres.get(key) || { acres: 0, county: up(u.county) || null };
    e.acres += n(u.acres) || 0;
    if (!e.county) e.county = up(u.county) || null;
    unitAcres.set(key, e);
  }

  // ── machine yield per CLU ──────────────────────────────────────────────────
  const jdByClu = new Map();
  let jdRowsNoClu = 0, jdAcresNoClu = 0, notCombinedRows = 0, notCombinedAcres = 0;
  for (const r of jdFtf) {
    if (r.metric !== "yield") continue;             // seed_rate reaches 221,282 seeds/ac
    // A chopper files corn as "CORN SILAGE"; it is read here only so it can be counted below.
    if (!sameCrop(r.crop, CROP) && up(r.crop) !== `${CROP} SILAGE`) continue;
    const y = r.operation_date ? new Date(r.operation_date).getUTCFullYear() : null;
    if (y !== CY) continue;
    const ac = n(r.acres), mv = n(r.mean_value);
    if (!(ac > 0) || !(mv > 0)) continue;
    // ⚠ ONLY A COMBINED PASS IS GRAIN (harvestForm.js, the one rule for this). A pass whose
    // stated unit contradicts its label is not a monitor figure either. Counted, never summed.
    if (harvestFormOf(r) !== "combined") { notCombinedRows++; notCombinedAcres += ac; continue; }
    const clu = cluKey(r.clu_id);
    // ⚠ A PASS THE ATTRIBUTION COULD NOT PLACE IS UNKNOWN, NOT UNINSURED — and it cannot be
    // put on a unit, because nothing says which. Counted so a unit that reads thin can be
    // told apart from a farm whose geometry failed.
    if (!clu) { jdRowsNoClu++; jdAcresNoClu += ac; continue; }
    const e = jdByClu.get(clu) || { acres: 0, qty: 0, grids: 0 };
    e.acres += ac; e.qty += ac * mv; e.grids++;
    jdByClu.set(clu, e);
  }

  // ── one row per unit ──────────────────────────────────────────────────────
  const JD = PR.codeForEvidence("jd_yield");          // RMA "Automated Yield Monitoring System"
  const units = [];
  for (const e of byUnit.values()) {
    const carrier = unitAcres.get(e.groupKey) || null;
    const insuredAcres = carrier && carrier.acres > 0 ? carrier.acres : e.insuredAcresFields;
    const acresSource = carrier && carrier.acres > 0 ? "carrier_unit" : "policy_fields";
    let machineAcres = 0, qtyCents = 0, clusWithYield = 0;
    for (const clu of e.clus) {
      const j = jdByClu.get(clu);
      if (!j) continue;
      machineAcres += j.acres; qtyCents += cents(j.qty); clusWithYield++;
    }
    const quantity = clusWithYield ? undo(qtyCents) : null;
    const coverage = insuredAcres > 0 ? machineAcres / insuredAcres : null;

    const ppAcres = ppAcresByUnit.get(e.groupKey) || 0;
    const isPp = ppAcresByUnit.has(e.groupKey) && !(carrier && carrier.acres > 0);
    const unknownPlan = unknownPlanUnits.has(e.groupKey) && !unitAcres.has(e.groupKey)
      ? unknownPlanUnits.get(e.groupKey) : null;
    let fillable = false, why = null;
    if (isPp) {
      why = "prevented planting — this unit reports no production";
    } else if (unknownPlan) {
      // ⚠ A PLAN NAME IN NEITHER SET IS A QUESTION, NOT A DEFAULT. Treating it as base
      // double-counts the unit's acres and halves the yield per acre; treating it as an
      // overlay drops them and inflates the yield. One understates a ten-year APH and the
      // other overstates it, so neither is a safe guess and the unit is refused instead.
      why = `this unit carries an unrecognised plan "${unknownPlan}" — it is not known whether that row states the unit's acres or restates them`;
    } else if (!(insuredAcres > 0)) {
      why = "no insured acreage on this unit, so there is nothing to report production against";
    } else if (clusWithYield === NO_COVERAGE) {
      // ⚠ NOT ZERO. Zero production on a planted unit is a total loss, the largest thing a
      // policy can pay. Absent evidence is absent, and it says so.
      why = `no machine yield on any of this unit's ${e.clus.size} insured field${e.clus.size === 1 ? "" : "s"}`;
    } else if (coverage != null && coverage > OVER_COVER_TOL) {
      // ⚠ MORE MACHINE ACRES THAN THE UNIT INSURES IS NOT A BETTER FIGURE, IT IS A WRONG ONE.
      // `jd_field_ftf` attributes by AREA OVERLAP and carries every acre the combine drove,
      // so a unit reading over 100% is either ground insured to another crop, a neighbour's,
      // or the same ground counted twice by two passes — and the production that comes with
      // those acres would be reported on this unit. The 2% tolerance is the figure
      // `productionEvidence` already uses for this exact comparison, not a new one.
      why = `the machine ran ${Math.round(coverage * 100)}% of this unit's insured acres — more ground than the unit insures, so some of that production belongs elsewhere`;
    } else if (floor != null && coverage != null && coverage < floor) {
      why = `the machine covered ${Math.round(coverage * 100)}% of this unit's insured acres, below the ${Math.round(floor * 100)}% this run required`;
    } else {
      fillable = true;
    }

    units.push({
      unitKey: e.unitKey, policyId: e.policyId, unitNumber: e.unitNumber, subUnit: e.subUnit,
      county: (carrier && carrier.county) || e.county || null,
      insuredAcres: +insuredAcres.toFixed(2), acresSource,
      // Our own field rows, kept beside the carrier's figure rather than folded into it: when
      // the two disagree the unit's acreage is in question and a yield per acre off either is
      // not checkable against the other.
      fieldAcres: +e.insuredAcresFields.toFixed(2),
      clus: e.clus.size, clusWithYield,
      machineAcres: +machineAcres.toFixed(2),
      coverage: coverage == null ? null : +coverage.toFixed(4),
      coveragePct: coverage == null ? null : Math.round(coverage * 100),
      quantity,
      // ⚠ OVER THE INSURED ACRES, NOT THE ACRES THE MACHINE COVERED. Dividing by the covered
      // acres prints the yield the combine saw on the ground it mapped, which is HIGHER than
      // the unit's yield per insured acre and overstates the APH this report sets. The form's
      // denominator is its own printed acreage; this matches it.
      yieldPerAcre: quantity != null && insuredAcres > 0 ? +(quantity / insuredAcres).toFixed(2) : null,
      recordType: quantity != null ? JD.code : null,
      recordTypeNeedsHuman: quantity != null ? JD.needsHuman : null,
      preventPlant: isPp,
      // Prevented acres beside the planted ones, named so the planted part's yield per acre
      // can be read against the form's planted acreage.
      ppAcres: +ppAcres.toFixed(2),
      unknownPlan,
      fillable, why,
    });
  }

  units.sort((a, b) => a.unitKey.localeCompare(b.unitKey) || String(a.policyId || "").localeCompare(String(b.policyId || "")));
  const fillableUnits = units.filter((u) => u.fillable);
  const refused = units.filter((u) => !u.fillable);

  return {
    crop: CROP, cropYear: CY,
    // ⚠ NAMED FROM THE SHARED VOCABULARY, so this cannot become a third opinion about what a
    // production figure IS. Every quantity below is the machine's — an alternative to the
    // documented records, never an addition to them, and not the claim of record.
    basis: BASIS.monitor.key,
    basisLabel: BASIS.monitor.label,
    basisWhy: BASIS.monitor.why,
    units,
    // ⚠ COUNTS, NOT A VERDICT. A farm where 8 of 10 units can be stated is not "ready" and is
    // not "blocked" — it is eight units of real evidence and two the machine never reached,
    // and the screen has to be able to say exactly that.
    totals: {
      units: units.length,
      fillable: fillableUnits.length,
      refused: refused.length,
      preventPlant: units.filter((u) => u.preventPlant).length,
      insuredAcres: +units.reduce((s, u) => s + u.insuredAcres, 0).toFixed(2),
      fillableAcres: +fillableUnits.reduce((s, u) => s + u.insuredAcres, 0).toFixed(2),
      // Only over the units that can be stated. A farm total that silently summed the
      // refused units as zero would be the understatement this module exists to refuse.
      fillableQuantity: fillableUnits.length
        ? undo(fillableUnits.reduce((s, u) => s + cents(u.quantity), 0)) : null,
    },
    // Reported, never folded into a unit: ground we hold acreage for and cannot attribute,
    // and machine passes we cannot place.
    unplaced: {
      fieldsWithNoUnit: fieldsNoUnit,
      machineRowsWithNoClu: jdRowsNoClu,
      machineAcresWithNoClu: +jdAcresNoClu.toFixed(1),
      // Chopped (silage) or contradicting passes under this crop's label — never grain.
      machineRowsNotCombined: notCombinedRows,
      machineAcresNotCombined: +notCombinedAcres.toFixed(1),
      silageFieldsSkipped,
    },
    recordTypeSource: PR.SOURCE,
  };
}

// ── WHAT THE RULES ABOVE ACTUALLY READ ───────────────────────────────────────
// A rule reading a field its own query never selected is a dead guard that passes every test
// — `check-query-contract` exists for exactly that, and this book has shipped it twice.
const PRODUCTION_UNITS_NEEDS = Object.freeze({
  policy_fields: ["policy_id", "clu_id", "crop", "crop_year", "unit_number", "sub_unit", "acres", "county", "type_code"],
  jd_field_ftf: ["clu_id", "crop", "metric", "operation_date", "acres", "mean_value"],
  // ⚠ `plan` IS LOAD-BEARING, NOT DECORATION. Omit it and every row classifies as unknown,
  // every unit is refused, and the page reads as a book with no machine data in it.
  crop_coverage_units: ["policy_id", "crop", "crop_year", "unit_number", "sub_unit", "acres", "county", "is_prevent_plant", "plan", "type_code"],
});

module.exports = {
  buildProductionUnits, unitKeyOf, unitPlanClass,
  BASE_UNIT_PLANS, OVERLAY_UNIT_PLANS, PRODUCTION_UNITS_NEEDS,
};
